import React, { useEffect, useState, useRef } from 'react';
import { useCollect, useRequest } from 'hooks';

import Show from 'components/Show';
import Modal from 'components/Modal';
import Nothing from 'components/Misc/Nothing';
import Progress from 'components/Misc/Progress';
import { Button } from 'components/Misc/Form';

import Dropzone from 'components/Browser/components/Dropzone';
import Toolbar from 'components/Browser/components/Toolbar';
import Files from 'components/Browser/components/Files';

const Browser = ({ visible, path, folder, onSelect, onClose }) => {
	const [files, setFiles] = useState([]);

	// Store requested files
	const [[collection, progress], collect] = useCollect(`browser/${path}/${folder}`);

	// Store upload request
	const [[uploading, percentage], upload] = useRequest('post', `browser/${path}/${folder}`);

	// Store remove request
	const [[removing], remove] = useRequest('delete', 'browser');

	// Cache dropping state
	const [dropping, setDropping] = useState(false);

	// Cache selected file
	const [active, setActive] = useState('');
	
	// Fix for stuttering
	const drags = useRef(0);

	// Handle drag enter event
	// @event: object | default onDragEnter event
	const onDragEnter = event => {
		event.preventDefault();

		// Update dropping state
		setDropping(true);

		// Update drag counter
		drags.current += 1;
	};

	// Handle drag leave event
	// @event: object | default onDragLeave event
	const onDragLeave = event => {
		event.preventDefault();

		// Update drag counter
		drags.current -= 1;

		// Do not update dropping state
		if (drags.current >= 0) return;

		// Update dropping state
		setDropping(false);
	};

	// Handle drag leave event
	// @event: object | default onDragEnd event
	const onDragEnd = event => {
		event.preventDefault();

		// Update dropping state
		setDropping(false);

		// Update drag counter
		drags.current = 0;
	};

	// Handle drop event
	// @event: object | default onDrop event
	const onDrop = event => {
		event.preventDefault();
		
		// Reset dropping state
		setDropping(false);

		// Reset drag counter
		drags.current = 0;

		// Upload dropped files
		// onUpload(event.dataTransfer.files);
	};

	// Handle file upload event
	// @event: object | default onChange event
	const onUpload = async (files) => {
		// Create formdata instance
		const data = new FormData();

		// Append all selected files
		Array.from(files).forEach(file => data.append('files', file));

		// Upload selected files
		const [success, error] = await upload(data);

		if (success) return setFiles(prev => ([
			...prev,
			...success.files
		]));

		if (error) console.log(error);
	};

	// Handle file delete event
	// @event: object | default onClick event
	const onDelete = async (event) => {
		event.preventDefault();

		// Remove selected file
		const [success, error] = await remove({
			file: `${path}/${folder}/${active}`
		});

		if (success) return setFiles(prev => prev.filter(file => file.name !== active));

		console.log(success, error);
	};

	// Collect files on mount and on parameter change
	useEffect(() => {
		if (visible) (async () => await collect())();
	}, [visible, collect]);

	// Update files on collection
	useEffect(() => {
		if (collection?.length) setFiles(collection);
	}, [collection]);

	useEffect(() => {
		if (!visible) setActive('');
	}, [visible]);

	// Do not display browser if not opened
	if (!visible) return null;

	return (
		<Modal title="Failu pārvaldnieks" visible={visible} handleModal={onClose}>
			<div
				className="space-y-3"
				onDragEnter={onDragEnter}
				onDragLeave={onDragLeave}
				onDragEnd={onDragEnd}
				onDrop={onDrop}
			>
				{/* Browser's control toolbar */}
				<Toolbar
					active={active}
					onUpload={onUpload}
					onDelete={onDelete}
					percentage={percentage}
					uploading={uploading}
					removing={removing}
				/>

				{/* Show loading progress */}
				<Progress progress={progress} />

				{/* Show nothing found */}
				<Nothing visible={!files.length && !progress} />

				{/* Files collection */}
				<Show if={files.length}>
					<Files
						active={active}
						files={files}
						progress={progress}
						onSelect={onSelect}
						setActive={setActive}
					/>
				</Show>

				{/* Dropzone component */}
				<Dropzone dropping={dropping} />

				{/* Action bar */}
				<div className="flex justify-end">
					<Button
						value="Ievietot"
						icon="check"
						theme="success"
						onClick={() => onSelect(active)}
						disabled={active === ''}
					/>
				</div>
			</div>
		</Modal>
	);
};

export default Browser;
